import { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ArrowRightOutlined, CheckCircleFilled, ExportOutlined, MessageOutlined, BulbOutlined } from '@ant-design/icons';
import { riseIn } from 'components/ui/styleKit';
import { MONO_FONT, RADIUS, REDUCED_MOTION } from 'components/ui/tokens';
import { SUPPORT_STEP } from '../content';
import { InterventionAction, INTERVENTIONS } from '../interventions';
import { ExitAnswer, exitReasonLabel } from '../reasons';
import { REASON_ICONS } from './reasonIcons';
import ToneButton from './ToneButton';

// The offer card is midnight in both app themes, so its contrast is decided here.
const MIDNIGHT = {
  surface: '#070B12',
  high: '#F2F6FB',
  medium: '#9FB0C4',
  hairline: 'rgba(255,255,255,0.08)',
  accent: '#A8E4FF',
  accentSoft: 'rgba(168,228,255,0.14)',
  accentLine: 'rgba(168,228,255,0.32)',
  glow: 'rgba(120,200,255,0.34)'
} as const;

// Hidden from screen readers, so each button is named by its label alone.
const ACTION_ICONS: Record<InterventionAction['kind'], { start: ReactNode; end: ReactNode }> = {
  support: { start: <MessageOutlined aria-hidden />, end: <ArrowRightOutlined aria-hidden /> },
  navigate: { start: <BulbOutlined aria-hidden />, end: <ExportOutlined aria-hidden /> }
};

// One line of an offer point, in px, shared by the text and its tick.
const POINT_LINE = '21px';

const monoLabelSx = { fontFamily: MONO_FONT, fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' } as const;

type Props = {
  answer: ExitAnswer;
  onAction: (action: InterventionAction) => void;
};

/** The offer, on its own midnight card: their answer read back, what we can do, and the one way to get it. */
export default function CancellationSupportStep({ answer, onAction }: Props) {
  const { badge, points, actionLabel, action } = INTERVENTIONS[answer.reason];
  const icons = ACTION_ICONS[action.kind];
  return (
    <Box
      sx={{
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        textAlign: 'left',
        px: { xs: 2.25, sm: 3 },
        py: 3,
        borderRadius: RADIUS.card,
        color: MIDNIGHT.high,
        bgcolor: MIDNIGHT.surface,
        border: `1px solid ${MIDNIGHT.hairline}`,
        // The aurora drifts slowly behind the content.
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '-30%',
          zIndex: -1,
          backgroundImage: [
            `radial-gradient(45% 40% at 85% 15%, ${MIDNIGHT.glow}, transparent 70%)`,
            `radial-gradient(40% 40% at 10% 90%, ${MIDNIGHT.accentSoft}, transparent 70%)`
          ].join(','),
          animation: 'auroraDrift 9s ease-in-out infinite alternate'
        },
        '@keyframes auroraDrift': {
          from: { transform: 'translate3d(-4%, -2%, 0) rotate(0deg)' },
          to: { transform: 'translate3d(4%, 3%, 0) rotate(6deg)' }
        },
        [REDUCED_MOTION]: { '&::before': { animation: 'none' } }
      }}
    >
      <Box
        component="span"
        sx={{
          ...monoLabelSx,
          display: 'inline-flex',
          px: 1.1,
          py: 0.5,
          borderRadius: RADIUS.pill,
          color: MIDNIGHT.accent,
          bgcolor: MIDNIGHT.accentSoft,
          border: `1px solid ${MIDNIGHT.accentLine}`
        }}
      >
        {badge}
      </Box>

      {/* Read back, not asked again: the reason step already collected it. */}
      <Box sx={{ mt: 2.25, pl: 1.75, borderLeft: `3px solid ${MIDNIGHT.accentLine}` }}>
        <Typography sx={{ ...monoLabelSx, color: MIDNIGHT.medium }}>{SUPPORT_STEP.youToldUs}</Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Box aria-hidden sx={{ display: 'flex', color: MIDNIGHT.accent, fontSize: 14 }}>
            {REASON_ICONS[answer.reason]}
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{exitReasonLabel(answer.reason)}</Typography>
        </Stack>
        {answer.comment && (
          <Typography sx={{ mt: 0.5, fontSize: 13, fontStyle: 'italic', color: MIDNIGHT.medium, overflowWrap: 'anywhere' }}>
            {SUPPORT_STEP.quote(answer.comment)}
          </Typography>
        )}
      </Box>

      <Stack component="ul" spacing={1.1} sx={{ mt: 2.5, mb: 0, p: 0, listStyle: 'none' }}>
        {points.map((point, index) => (
          <Stack component="li" key={point} direction="row" spacing={1.25} alignItems="flex-start" sx={riseIn(index + 4, { step: 60 })}>
            {/* As tall as one line of the text, so the tick centres on its first line. */}
            <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', height: POINT_LINE, fontSize: 14, color: MIDNIGHT.accent }}>
              <CheckCircleFilled />
            </Box>
            <Typography sx={{ fontSize: 14, lineHeight: POINT_LINE }}>{point}</Typography>
          </Stack>
        ))}
      </Stack>

      <Box sx={{ mt: 3, '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
        <ToneButton tone="inverse" label={actionLabel} startIcon={icons.start} endIcon={icons.end} onClick={() => onAction(action)} />
      </Box>
    </Box>
  );
}
